/**
 * Client Detail Page
 *
 * Mobile-first detail view for a single client.
 */

import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useInfiniteQuery } from '@tanstack/react-query'
import { ArrowLeft, Pencil, DollarSign, Phone, Mail, MapPin, CreditCard, Users, Trash2, RotateCcw, Receipt, ChevronDown, ChevronUp, FileText } from 'lucide-react'

import api from '@/lib/api'
import { useClient, useDeleteClient, useRestoreClient } from '@/hooks/useClients'
import { formatCurrency, formatDate, getIdentificationTypeLabel, PAYMENT_TYPE_LABELS } from '@/lib/utils'
import { t } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { QueryError } from '@/components/ui/query-error'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const clientId = id && !isNaN(Number(id)) ? Number(id) : 0
  const { data: client, isLoading, error, refetch } = useClient(clientId)

  // Fetch real balance from account-summary (calculated from bills, not cached)
  const { data: accountSummary } = useQuery<{
    total_debt: number
    available_credit: number
    credit_limit: number
    pending_bills_count: number
    available_balance: number
  }>({
    queryKey: ['client-account-summary', clientId],
    queryFn: async () => {
      const response = await api.get(`/clients/${clientId}/account-summary`)
      return response.data
    },
    enabled: !!clientId,
  })

  // Fetch deposit history with pagination
  const PAGE_SIZE = 10
  const {
    data: depositsPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError: depositsError,
    refetch: refetchDeposits,
  } = useInfiniteQuery({
    queryKey: ['client-deposits', clientId],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await api.get(
        `/clients/${clientId}/deposits?show_all=true&page=${pageParam}&page_size=${PAGE_SIZE}`
      )
      return response.data as {
        items: Array<{
          id: number
          amount: number
          remaining_amount: number
          payment_type: string | null
          reference: string | null
          notes: string | null
          is_fully_applied: boolean
          created_at: string
          applied_bill_count: number
          applied_bill_numbers: string[]
          applied_bill_ids: number[]
        }>
        total: number
        page: number
        pages: number
      }
    },
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.pages ? lastPage.page + 1 : undefined,
    initialPageParam: 1,
    enabled: !!clientId,
  })

  const allDeposits = depositsPages?.pages.flatMap((p) => p.items) ?? []
  const totalDeposits = depositsPages?.pages[0]?.total ?? 0

  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [expandedDepositId, setExpandedDepositId] = useState<number | null>(null)

  // Fetch deposit detail when expanded
  const { data: depositDetail, isLoading: isLoadingDetail } = useQuery<{
    id: number
    amount: number
    applied_bills: Array<{
      bill_id: number
      bill_number: string | null
      bill_total: number
      amount_applied: number
      remaining_after: number
      created_at: string
    }>
  }>({
    queryKey: ['client-deposit-detail', clientId, expandedDepositId],
    queryFn: async () => {
      const response = await api.get(`/clients/${clientId}/deposits/${expandedDepositId}`)
      return response.data
    },
    enabled: !!expandedDepositId,
  })

  const deleteClient = useDeleteClient()
  const restoreClient = useRestoreClient()

  const isDeleted = !!client?.deleted_at
  const hasBills = (client?.bill_count ?? 0) > 0

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  if (error || !client) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Users className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <p className="text-lg font-medium mb-2">{t('clients.clientNotFound')}</p>
        <p className="text-sm text-muted-foreground mb-4">
          {error ? t('clients.errorLoadingClient') : t('clients.clientDoesNotExist')}
        </p>
        <Button variant="outline" onClick={() => navigate('/clients')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('clients.backToClients')}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={() => navigate('/clients')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-bold truncate">{client.name}</h1>
          <p className="text-sm text-muted-foreground">
            {getIdentificationTypeLabel(client.type_identification)} - {client.identification}
          </p>
        </div>
      </div>

      {/* Deleted Banner */}
      {isDeleted && (
        <div className="flex items-center justify-between p-3 rounded-lg bg-destructive/10 border border-destructive/20">
          <div className="flex items-center gap-2">
            <Trash2 className="h-4 w-4 text-destructive" />
            <span className="text-sm font-medium text-destructive">{t('clients.clientDeleted')}</span>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              await restoreClient.mutateAsync(Number(id))
              refetch()
            }}
            disabled={restoreClient.isPending}
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            {t('clients.restoreClient')}
          </Button>
        </div>
      )}

      {/* Quick Actions */}
      {!isDeleted && (
        <div className="flex gap-2">
          <Button asChild className="flex-1 h-12">
            <Link to={`/clients/${id}/edit`}>
              <Pencil className="h-4 w-4 mr-2" />
              {t('common.edit')}
            </Link>
          </Button>
          <Button
            variant="outline"
            className="flex-1 h-12"
            disabled={!accountSummary || (accountSummary.total_debt ?? 0) <= 0}
            asChild={!!(accountSummary && (accountSummary.total_debt ?? 0) > 0)}
          >
            {accountSummary && (accountSummary.total_debt ?? 0) > 0 ? (
              <Link to={`/clients/${id}/deposit`}>
                <DollarSign className="h-4 w-4 mr-2" />
                {t('clients.deposit')}
              </Link>
            ) : (
              <span>
                <DollarSign className="h-4 w-4 mr-2" />
                {t('clients.deposit')}
              </span>
            )}
          </Button>
          <Button
            variant="outline"
            className="h-12 w-12 sm:w-auto shrink-0 text-destructive hover:text-destructive"
            onClick={() => setShowDeleteDialog(true)}
            aria-label={t('clients.deleteClient')}
          >
            <Trash2 className="h-4 w-4 sm:mr-2" aria-hidden="true" />
            <span className="hidden sm:inline">{t('clients.deleteClient')}</span>
          </Button>
        </div>
      )}

      {/* Contact Info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('clients.contact')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {client.phone && (
            <a
              href={`tel:${client.phone}`}
              className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 active:bg-muted"
            >
              <Phone className="h-5 w-5 text-muted-foreground" />
              <span>{client.phone}</span>
            </a>
          )}
          {client.celphone && (
            <a
              href={`tel:${client.celphone}`}
              className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 active:bg-muted"
            >
              <Phone className="h-5 w-5 text-muted-foreground" />
              <span>{client.celphone} ({t('clients.celphone')})</span>
            </a>
          )}
          {client.email && (
            <a
              href={`mailto:${client.email}`}
              className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 active:bg-muted"
            >
              <Mail className="h-5 w-5 text-muted-foreground" />
              <span className="truncate">{client.email}</span>
            </a>
          )}
          {client.address && (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
              <MapPin className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
              <span>{client.address}</span>
            </div>
          )}
          {!client.phone && !client.email && !client.address && (
            <p className="text-sm text-muted-foreground text-center py-2">
              {t('clients.noContactInfo')}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Credit Info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            {t('clients.credit')}
            {client.credit_blocked && (
              <span className="ml-auto rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-950/40 dark:text-red-300">
                {t('clients.creditBlockedBadge')}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 rounded-lg bg-muted/50">
              <p className="text-xs text-muted-foreground mb-1">{t('clients.creditLimit')}</p>
              <p className="text-lg font-bold">{formatCurrency(client.credit_limit)}</p>
            </div>
            <div className="p-3 rounded-lg bg-muted/50">
              <p className="text-xs text-muted-foreground mb-1">{t('clients.paymentDays')}</p>
              <p className="text-lg font-bold">{client.payment_days} {t('clients.days')}</p>
            </div>
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-100 dark:border-red-900/30">
              <p className="text-xs text-muted-foreground mb-1">{t('deposits.totalDebt')}</p>
              <p className={`text-2xl font-bold ${(accountSummary?.total_debt || 0) > 0 ? 'text-red-500 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                {formatCurrency(accountSummary?.total_debt ?? client.current_balance)}
              </p>
              {(accountSummary?.pending_bills_count || 0) > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  {accountSummary?.pending_bills_count} {t('deposits.pendingBillsCount')}
                </p>
              )}
            </div>
            <div className="p-3 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-100 dark:border-green-900/30">
              <p className="text-xs text-muted-foreground mb-1">{t('deposits.availableCredit')}</p>
              <p className="text-lg font-bold text-green-600 dark:text-green-400">
                {formatCurrency(accountSummary?.available_credit ?? 0)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Deposit History */}
      {depositsError && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Receipt className="h-4 w-4" aria-hidden="true" />
              {t('deposits.depositHistory')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <QueryError onRetry={() => refetchDeposits()} />
          </CardContent>
        </Card>
      )}
      {allDeposits.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Receipt className="h-4 w-4" />
              {t('deposits.depositHistory')} ({totalDeposits})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {allDeposits.map((deposit) => {
              const isExpanded = expandedDepositId === deposit.id
              const detailReady = depositDetail && depositDetail.id === deposit.id
              return (
                <div key={deposit.id} className="rounded-lg bg-muted/50 overflow-hidden">
                  <button
                    type="button"
                    className="w-full flex justify-between items-center p-3 hover:bg-muted/80 transition-colors"
                    onClick={() => setExpandedDepositId(isExpanded ? null : deposit.id)}
                  >
                    <div className="text-left">
                      <p className="font-medium text-green-600 dark:text-green-400">{formatCurrency(deposit.amount)}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(deposit.created_at)} - {PAYMENT_TYPE_LABELS[deposit.payment_type || ''] || deposit.payment_type || '-'}
                      </p>
                      {deposit.applied_bill_count > 0 && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <FileText className="h-3 w-3" />
                          {deposit.applied_bill_count === 1
                            ? (deposit.applied_bill_numbers[0] || `#${deposit.applied_bill_ids[0]}`)
                            : `${deposit.applied_bill_count} ${t('deposits.bills') || 'facturas'}`}
                        </p>
                      )}
                      {deposit.reference && (
                        <p className="text-xs text-muted-foreground">Ref: {deposit.reference}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        {deposit.is_fully_applied ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                            {t('deposits.fullyApplied')}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            {t('deposits.remaining')}: {formatCurrency(deposit.remaining_amount)}
                          </span>
                        )}
                      </div>
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="px-3 pb-3 border-t border-border/50">
                      {(isLoadingDetail || !detailReady) ? (
                        <p className="text-xs text-muted-foreground py-2 text-center">...</p>
                      ) : detailReady && depositDetail.applied_bills.length > 0 ? (
                        <div className="mt-2 space-y-1">
                          <p className="text-xs font-medium text-muted-foreground mb-1">
                            {t('deposits.appliedTo')} ({depositDetail.applied_bills.length})
                          </p>
                          {depositDetail.applied_bills.map((bill) => (
                            <Link
                              key={bill.bill_id}
                              to={`/bills/${bill.bill_id}`}
                              className="flex items-center justify-between p-2 rounded bg-background hover:bg-accent transition-colors text-sm"
                            >
                              <div className="flex items-center gap-2">
                                <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                                <span>{bill.bill_number || `#${bill.bill_id}`}</span>
                              </div>
                              <div className="text-right">
                                <span className="font-medium text-green-600 dark:text-green-400">
                                  -{formatCurrency(bill.amount_applied)}
                                </span>
                                {bill.remaining_after <= 0 ? (
                                  <p className="text-xs text-green-600 dark:text-green-400">{t('deposits.paidInFull')}</p>
                                ) : (
                                  <p className="text-xs text-muted-foreground">
                                    {t('deposits.remaining')}: {formatCurrency(bill.remaining_after)}
                                  </p>
                                )}
                              </div>
                            </Link>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground py-2 text-center">
                          {t('deposits.notAppliedToBills')}
                        </p>
                      )}
                      {detailReady && (
                        <Link
                          to={`/clients/${clientId}/deposits/${deposit.id}`}
                          className="mt-2 flex items-center justify-center gap-2 p-2 rounded bg-background hover:bg-accent transition-colors text-sm font-medium"
                        >
                          <Receipt className="h-3.5 w-3.5" />
                          {t('deposits.viewDetails')}
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              )
            })}

            {/* Load More */}
            {hasNextPage && (
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? '...' : t('common.loadMore')}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Exoneration Info (if applicable) */}
      {client.is_exonerated && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('clients.fiscalExoneration')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">{t('clients.type')}</span>
              <span className="text-sm font-medium">{client.exoneration_type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">{t('clients.number')}</span>
              <span className="text-sm font-medium">{client.exoneration_number}</span>
            </div>
            {client.exoneration_percentage && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">{t('clients.percentage')}</span>
                <span className="text-sm font-medium">{client.exoneration_percentage}%</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('clients.deleteClient')}: {client.name}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p className="font-medium text-foreground">{client.identification}</p>
                <p>
                  {hasBills
                    ? t('clients.confirmDeleteHasInvoices')
                    : t('clients.confirmDeletePermanent')
                  }
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                await deleteClient.mutateAsync({ id: Number(id) })
                setShowDeleteDialog(false)
                refetch()
              }}
            >
              {t('clients.deactivate')}
            </AlertDialogAction>
            {!hasBills && (
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={async () => {
                  await deleteClient.mutateAsync({ id: Number(id), permanent: true })
                  setShowDeleteDialog(false)
                  navigate('/clients')
                }}
              >
                {t('clients.deletePermanently')}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
